import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Article 1 - 初學者的 Omakase 完整指南
 * ✅ 移除重複的 <Helmet> FAQPage Schema
 *    → 此頁面由 ArticlePage.jsx 統一注入 Article Schema（@graph 格式）
 *    → 文章內的 FAQ 使用 HTML microdata 標記（不另注入 JSON-LD，避免頁面型別混淆）
 */
export default function Article1() {
  return (
    <div className="prose prose-lg dark:prose-invert max-w-none">
      {/* 引言 */}
      <section className="mb-12">
        <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
          Omakase（お任せ），字面意思是「我交給你」，代表著對廚師的完全信任。這不僅是一種用餐方式，更是一場文化體驗之旅。對於初次嘗試的人來說，Omakase 可能令人既期待又緊張。本文將為您詳細解說從預約到用餐的每一個環節，讓您能夠自信地享受這場獨特的美食體驗。
        </p>
      </section>

      {/* 第一部分：什麼是 Omakase */}
      <section className="mb-16">
        <h2 id="what-is-omakase" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-orange-500">
          什麼是 Omakase？
        </h2>
        <div className="bg-gradient-to-br from-orange-50 to-rose-50 dark:from-gray-800 dark:to-gray-700 p-8 rounded-2xl mb-8">
          <h3 id="omakase-definition" className="text-2xl font-bold text-gray-900 dark:text-white mb-4">核心概念</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Omakase 源自日本，是「お任せします」（omakase shimasu）的簡稱，意思是「我把決定權交給你」。在餐飲情境中，這代表客人將菜單選擇完全交給廚師，由廚師根據當日最新鮮的食材、季節特色以及客人的喜好，精心設計一套完整的料理體驗。
          </p>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl">
            <h4 className="font-bold text-gray-900 dark:text-white mb-3">Omakase 的三大核心價值：</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start"><span className="text-orange-500 mr-2">▪</span><span><strong>信任</strong>：客人對廚師專業的完全信賴</span></li>
              <li className="flex items-start"><span className="text-orange-500 mr-2">▪</span><span><strong>季節性</strong>：強調當季最佳食材的運用</span></li>
              <li className="flex items-start"><span className="text-orange-500 mr-2">▪</span><span><strong>客製化</strong>：根據客人偏好調整的個人化體驗</span></li>
            </ul>
          </div>
        </div>

        <h3 id="omakase-vs-regular" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 mt-10">Omakase 與一般用餐的差異</h3>
        <div className="overflow-x-auto mb-8">
          <table className="w-full border-collapse bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg">
            <thead>
              <tr className="bg-gradient-to-r from-orange-500 to-rose-500 text-white">
                <th className="p-4 text-left font-bold">比較項目</th>
                <th className="p-4 text-left font-bold">Omakase</th>
                <th className="p-4 text-left font-bold">一般用餐</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {[
                ['菜單選擇', '由廚師決定', '客人自行選擇'],
                ['食材來源', '當日最佳食材', '固定菜單食材'],
                ['上菜節奏', '廚師控制，循序漸進', '客人決定點餐順序'],
                ['互動程度', '高度互動，廚師會解說', '較少互動'],
                ['價格透明度', '通常為固定套餐價', '依點餐內容計價'],
                ['體驗重點', '完整的料理故事', '滿足個人喜好'],
              ].map(([item, omakase, regular]) => (
                <tr key={item} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="p-4 font-medium text-gray-900 dark:text-white">{item}</td>
                  <td className="p-4 text-gray-700 dark:text-gray-300">{omakase}</td>
                  <td className="p-4 text-gray-700 dark:text-gray-300">{regular}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 如何預約 */}
      <section className="mb-16">
        <h2 id="how-to-book" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-orange-500">
          如何預約 Omakase 餐廳
        </h2>
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 mb-8 rounded-r-xl">
          <p className="text-blue-900 dark:text-blue-300 font-medium mb-2">💡 <strong>重要提醒</strong></p>
          <p className="text-blue-800 dark:text-blue-200">大多數 Omakase 餐廳採取預約制，且座位有限。建議至少提前 1-2 週預約，熱門餐廳可能需要提前 1-3 個月。</p>
        </div>

        <h3 id="booking-channels" className="text-2xl font-bold text-gray-900 dark:text-white mb-6">預約管道</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {[
            { icon: '📞', title: '電話預約', color: 'orange', items: ['最直接的溝通方式', '可立即確認座位', '適合詳細說明需求', '建議在非用餐時段致電'] },
            { icon: '💻', title: '線上預約', color: 'blue', items: ['24小時皆可預約', '可查看即時空位', '保留電子確認紀錄', '部分餐廳提供優惠'] },
          ].map(({ icon, title, color, items }) => (
            <div key={title} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 bg-${color}-100 dark:bg-${color}-900/30 rounded-full flex items-center justify-center`}>
                  <span className={`text-${color}-600 dark:text-${color}-400 text-xl`}>{icon}</span>
                </div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h4>
              </div>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                {items.map(i => <li key={i} className="flex items-start"><span className={`text-${color}-500 mr-2`}>✓</span><span>{i}</span></li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* 價格指南 */}
      <section className="mb-16">
        <h2 id="price-guide" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-orange-500">
          Omakase 價格指南
        </h2>
        <div className="space-y-6 mb-10">
          {[
            { id: 'price-entry', label: '入門級', price: 'NT$ 1,500 - 3,000', color: 'green', items: ['8-12 道料理', '使用本地優質食材', '適合初次體驗', '用餐時間約 1.5 小時'], audience: ['Omakase 初學者', '預算有限的饕客', '想嘗試新體驗的人'] },
            { id: 'price-mid', label: '中階級', price: 'NT$ 3,000 - 6,000', color: 'blue', items: ['12-15 道料理', '部分進口高級食材', '廚師經驗豐富', '用餐時間約 2 小時'], audience: ['有基礎經驗者', '追求品質的饕客', '特殊場合慶祝'] },
            { id: 'price-high', label: '高階級', price: 'NT$ 6,000 - 12,000', color: 'purple', items: ['15-20 道料理', '大量日本直送食材', '知名廚師主理', '用餐時間約 2.5 小時'], audience: ['資深饕客', '重要商務宴請', '追求極致體驗'] },
            { id: 'price-premium', label: '頂級', price: 'NT$ 12,000+', color: 'amber', items: ['20+ 道精緻料理', '頂級稀有食材', '米其林等級廚師', '用餐時間 3+ 小時'], audience: ['美食收藏家', '人生重要時刻', '追求極致奢華'] },
          ].map(tier => (
            <div key={tier.id} className={`bg-gradient-to-r from-${tier.color}-50 to-${tier.color}-100/50 dark:from-${tier.color}-900/20 dark:to-${tier.color}-900/10 p-6 rounded-xl border-l-4 border-${tier.color}-500`}>
              <div className="flex items-center justify-between mb-4">
                <h3 id={tier.id} className="text-2xl font-bold text-gray-900 dark:text-white">{tier.label}</h3>
                <span className={`text-2xl font-bold text-${tier.color}-600 dark:text-${tier.color}-400`}>{tier.price}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">特色</h4>
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300 text-sm">{tier.items.map(i => <li key={i}>• {i}</li>)}</ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">適合對象</h4>
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300 text-sm">{tier.audience.map(a => <li key={a}>• {a}</li>)}</ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 用餐禮儀 */}
      <section className="mb-16">
        <h2 id="dining-etiquette" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-orange-500">
          Omakase 用餐禮儀
        </h2>
        <div className="space-y-8">
          {[
            { id: 'dress-code', icon: '👔', title: '服裝要求', good: ['Smart Casual 或商務休閒', '乾淨整潔的服裝', '適度正式的鞋子', '避免過度休閒'], bad: ['拖鞋、涼鞋', '短褲、背心', '過度破損的牛仔褲', '強烈香水'] },
            { id: 'eating-etiquette', icon: '🍣', title: '用餐細節', good: ['上桌後盡快享用，品嚐最佳狀態', '跟隨廚師節奏，不催促不拖延', '壽司可用手或筷子食用', '醬油輕沾魚肉面即可'], bad: ['讓料理在面前等待太久', '混合山葵和醬油', '米飯面沾醬油', '倒過多醬油'] },
          ].map(({ id, icon, title, good, bad }) => (
            <div key={id} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
              <h3 id={id} className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <span className="text-3xl">{icon}</span>{title}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-green-600 dark:text-green-400 mb-3">✓ 建議</h4>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">{good.map(i => <li key={i}>• {i}</li>)}</ul>
                </div>
                <div>
                  <h4 className="font-bold text-red-600 dark:text-red-400 mb-3">✗ 避免</h4>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">{bad.map(i => <li key={i}>• {i}</li>)}</ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ✅ FAQ 區塊：使用 HTML microdata 而非 JSON-LD，避免與 Article Schema 衝突 */}
      <section className="mb-16" itemScope itemType="https://schema.org/FAQPage">
        <h2 id="faq" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-orange-500">
          常見問題 FAQ
        </h2>
        <div className="space-y-4">
          {[
            {
              q: '第一次吃 Omakase 需要預約嗎？',
              a: '是的，Omakase 餐廳通常需要提前預約。因為廚師需要根據預約人數準備食材，且座位有限。建議至少提前 1-2 週預約，熱門餐廳可能需要提前 1-3 個月。預約時請告知人數、用餐時間、是否有飲食禁忌等資訊。',
            },
            {
              q: 'Omakase 的價格範圍大概是多少？',
              a: 'Omakase 價格範圍很廣：入門級約 NT$1,500-3,000，中階級約 NT$3,000-6,000，高階級約 NT$6,000-12,000，頂級可達 NT$12,000 以上。價格差異主要來自食材等級、廚師經驗和餐廳定位。',
            },
            {
              q: '用餐時可以拍照嗎？',
              a: '大多數餐廳允許拍照，但請遵守：關閉閃光燈和快門聲、不影響其他客人用餐、避免拍攝其他客人、不讓拍照影響用餐節奏。某些高級餐廳可能禁止拍照，請事先詢問。',
            },
            {
              q: '如果有食材過敏或不吃的東西怎麼辦？',
              a: '請在預約時就告知餐廳您的飲食限制或過敏原。廚師會根據您的需求調整菜單。但請注意，過多的限制可能會影響 Omakase 的體驗完整性。',
            },
            {
              q: 'Omakase 用餐時間大約多長？',
              a: '用餐時間依價位和類型而異：入門級約 1.5 小時，中階級約 2 小時，高階級約 2.5 小時，頂級 3 小時以上。壽司 Omakase 通常較短（1.5-2小時），懷石 Omakase 較長（2-3小時）。',
            },
            {
              q: '需要給小費嗎？',
              a: '在台灣，Omakase 餐廳通常已包含服務費，不需要額外給小費。如果對服務特別滿意，最好的感謝方式是真誠的讚美、下次再訪，以及在社群媒體上分享正面評價。',
            },
            {
              q: '可以帶小孩去嗎？',
              a: '這取決於餐廳政策和孩子的年齡。部分高級餐廳有年齡限制（如 12 歲以上）。需考慮孩子是否能安靜用餐 2-3 小時。預約時務必告知有孩童同行。',
            },
          ].map(({ q, a }) => (
            <div
              key={q}
              className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <details>
                <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors list-none">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white" itemProp="name">{q}</h3>
                  <span className="text-orange-500 ml-4 flex-shrink-0">▼</span>
                </summary>
                <div
                  className="p-6 pt-0 text-gray-700 dark:text-gray-300"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p className="leading-relaxed" itemProp="text">{a}</p>
                </div>
              </details>
            </div>
          ))}
        </div>
      </section>

      {/* 結語 */}
      <section className="mb-16">
        <h2 id="conclusion" className="text-3xl font-bold text-gray-900 dark:text-white mb-6">結語</h2>
        <div className="bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 p-8 rounded-2xl">
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            Omakase 不僅是一頓飯，更是一場文化體驗和信任的旅程。通過本指南，您已經了解了從預約到用餐的各個環節。記住，最重要的是保持開放的心態，尊重廚師的專業，並享受這個獨特的美食體驗。
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            每次 Omakase 體驗都是獨一無二的，即使是同一家餐廳，不同季節、不同日子的料理都會有所不同。這正是 Omakase 的魅力所在——每一次都是新的驚喜和發現。
          </p>
        </div>
      </section>

      {/* 延伸閱讀 */}
      <section className="mt-16 pt-8 border-t-2 border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">延伸閱讀</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { to: '/complete-omakase-etiquette-guide', icon: '🍱', title: 'Omakase 禮儀大全', desc: '深入了解 Omakase 用餐的各種禮儀細節', gradient: 'from-orange-400 to-rose-500', hover: 'orange' },
            { to: '/sushi-vs-kaiseki-omakase-comparison', icon: '🍣', title: '壽司 vs 懷石 Omakase', desc: '比較兩種不同風格的 Omakase 體驗', gradient: 'from-blue-400 to-cyan-500', hover: 'blue' },
            { to: '/premium-ingredients-analysis-from-tsukiji', icon: '🐟', title: '頂級食材解析', desc: '探索 Omakase 使用的頂級食材來源', gradient: 'from-purple-400 to-pink-500', hover: 'purple' },
          ].map(link => (
            <Link key={link.to} to={link.to} className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className={`h-48 bg-gradient-to-br ${link.gradient} flex items-center justify-center`}>
                <span className="text-6xl">{link.icon}</span>
              </div>
              <div className="p-6">
                <h3 className={`text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-${link.hover}-500 transition-colors`}>{link.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{link.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}